// @owners { team: patients }
import { useEffect, useRef, useState } from 'react';

type VersionResponse = {
  version: string;
};

function isVersionResponse(data: unknown): data is VersionResponse {
  return typeof data === 'object' && data !== null && typeof (data as Record<string, unknown>).version === 'string';
}

export const useVersionPoller = (): { isStale: boolean; detectedVersion: string | undefined } => {
  const [isStale, setIsStale] = useState(false);
  const [detectedVersion, setDetectedVersion] = useState<string | undefined>(undefined);
  const isStaleRef = useRef(false);
  const baselineVersion = useRef(process.env.WEB_VERSION);

  useEffect(() => {
    const version = baselineVersion.current;

    if (!version || version === 'unknown') {
      return;
    }

    const checkVersion = async () => {
      if (isStaleRef.current) return;
      try {
        const response = await fetch('/version.json');
        if (!response.ok) return;

        const data: unknown = await response.json();
        if (!isVersionResponse(data)) return;
        if (data.version !== version) {
          isStaleRef.current = true;
          setIsStale(true);
          setDetectedVersion(data.version);
        }
      } catch {
        // swallow silently — network errors should not affect the user
      }
    };

    // Intentionally no immediate check on mount — we only want to check when
    // the patient returns to a tab they left open, not on the initial page load.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkVersion();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return { isStale, detectedVersion };
};
